import React, { useMemo } from 'react';
import { Spin } from 'antd';
import { CMSContext, CMSContextValue, Language } from '@riptik/cms-backoffice-module';
import { useTenant } from '../contexts/TenantContext';
import { useTranslation } from '../hooks/useTranslation';
import { useEventContext } from '../contexts/EventContext';
import { LocalizationService, setTenantContext } from '../api/client';
import { CmsService } from '../api/generated/services/CmsService';

/**
 * Riptik CMS Provider
 *
 * Provider này THUỘC VỀ PARENT PROJECT (Riptik Backoffice)
 * Nó implement CMSContextValue interface bằng cách:
 * - Lấy data từ useTenant()
 * - Lấy translation từ useTranslation()
 * - Implement API functions bằng LocalizationService
 *
 * Nếu project khác (Mantik, NextBus...) muốn dùng CMS module,
 * họ chỉ cần tạo Provider riêng implement CMSContextValue interface
 */
export const RiptikCMSProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const tenant = useTenant();
  const { t } = useTranslation();
  const { getOrgUrl } = useEventContext();

  // Memoize context value để tránh re-render không cần thiết
  const contextValue = useMemo<CMSContextValue | null>(() => {
    // Return null if tenant not available yet (during initial load)
    if (!tenant) {
      return null;
    }

    return {
      // ==================== TENANT INFO ====================
      tenantId: tenant.organization?.id || tenant.organization_slug,
      organizationSlug: tenant.organization_slug,
      eventSlug: undefined,

      // ==================== LOCALIZATION ====================
      locale: 'en',
      availableLocales: tenant.supported_languages?.map(l => l.code) || ['en', 'vi'],
      t,

      // ==================== NAVIGATION ====================
      getOrgUrl,

      // ==================== API FUNCTIONS ====================
      api: {
        /**
         * Fetch all system languages using Riptik's LocalizationService
         */
        fetchLanguages: async (): Promise<Language[]> => {
          try {
            // Set tenant context cho API call
            setTenantContext(tenant);

            // Call Riptik API
            const response = await LocalizationService.getLanguages();

            // Parse response
            const languages = response?.languages || response || [];
            return Array.isArray(languages) ? languages : [];
          } catch (error) {
            console.error('Failed to fetch languages:', error);
            // Return default languages nếu API fail
            return [
              { code: 'en', name: 'English', native_name: 'English' },
              { code: 'vi', name: 'Vietnamese', native_name: 'Tiếng Việt' },
            ];
          }
        },

        /**
         * Fetch organization's configured languages using Riptik's LocalizationService
         */
        fetchOrgAvailableLanguages: async (
          organizationSlug: string
        ): Promise<string[]> => {
          try {
            // Set tenant context cho API call
            setTenantContext(tenant);

            // Call Riptik API
            const response = await LocalizationService.getOrganizersLocalization(
              organizationSlug
            );

            // Return org's available languages
            return response?.available_languages || ['en', 'vi'];
          } catch (error) {
            console.error('Failed to fetch org languages:', error);
            // Return defaults nếu API fail
            return ['en', 'vi'];
          }
        },

        // ========== Articles ==========
        fetchArticles: async (params) => {
          const response = await CmsService.getOrganizersCmsArticles(
            tenant.organization_slug,
            params.limit,
            params.offset,
            params.status,
            params.type
          );
          return {
            articles: response?.articles || [],
            count: response?.count || 0,
            total: response?.total || response?.count || 0,
          };
        },

        fetchArticle: async (articleId) => {
          return await CmsService.getOrganizersCmsArticles1(
            tenant.organization_slug,
            articleId
          );
        },

        createArticle: async (data) => {
          return await CmsService.postOrganizersCmsArticles(
            tenant.organization_slug,
            data
          );
        },

        updateArticle: async (articleId, data) => {
          return await CmsService.putOrganizersCmsArticles(
            tenant.organization_slug,
            articleId,
            data
          );
        },

        deleteArticle: async (articleId) => {
          await CmsService.deleteOrganizersCmsArticles(
            tenant.organization_slug,
            articleId
          );
        },

        // ========== Pages ==========
        fetchPages: async (params) => {
          const response = await CmsService.getOrganizersCmsPages(
            tenant.organization_slug,
            params.limit,
            params.offset,
            params.status
          );
          return {
            pages: response?.pages || [],
            count: response?.count || 0,
            total: response?.total || response?.count || 0,
          };
        },

        fetchPage: async (pageId) => {
          return await CmsService.getOrganizersCmsPages1(
            tenant.organization_slug,
            pageId
          );
        },

        createPage: async (data) => {
          return await CmsService.postOrganizersCmsPages(
            tenant.organization_slug,
            data
          );
        },

        updatePage: async (pageId, data) => {
          return await CmsService.putOrganizersCmsPages(
            tenant.organization_slug,
            pageId,
            data
          );
        },

        deletePage: async (pageId) => {
          await CmsService.deleteOrganizersCmsPages(
            tenant.organization_slug,
            pageId
          );
        },

        // ========== Tags ==========
        fetchTags: async () => {
          const response = await CmsService.getOrganizersCmsTags(
            tenant.organization_slug
          );
          return {
            tags: response?.tags || [],
            count: response?.count || 0,
            total: response?.total || response?.count || 0,
          };
        },

        createTag: async (data) => {
          return await CmsService.postOrganizersCmsTags(
            tenant.organization_slug,
            data
          );
        },

        updateTag: async (tagId, data) => {
          return await CmsService.putOrganizersCmsTags(
            tenant.organization_slug,
            tagId,
            data
          );
        },

        deleteTag: async (tagId) => {
          await CmsService.deleteOrganizersCmsTags(
            tenant.organization_slug,
            tagId
          );
        },

        // ========== Media ==========
        fetchImages: async (params) => {
          const response = await CmsService.getOrganizersCmsImages(
            tenant.organization_slug,
            params.limit,
            params.offset,
            params.search
          );
          return {
            images: response?.images || [],
            count: response?.count || 0,
            total: response?.total || response?.count || 0,
          };
        },

        fetchImage: async (imageId: string) => {
          return await CmsService.getOrganizersCmsImages1(
            tenant.organization_slug,
            imageId
          );
        },

        fetchCollectionImages: async (collectionId, params: any = {}) => {
          const response = await CmsService.getOrganizersCmsCollectionsMedia(
            tenant.organization_slug,
            collectionId,
            params.limit || 10,
            params.offset || 0
          );
          return {
            images: response?.media || [],
            count: response?.media?.length || 0,
            total: response?.media?.length || 0,
          };
        },

        updateImage: async (imageId, data) => {
          return await CmsService.putOrganizersCmsImages(
            tenant.organization_slug,
            imageId,
            data
          );
        },

        deleteImage: async (imageId) => {
          await CmsService.deleteOrganizersCmsImages(
            tenant.organization_slug,
            imageId
          );
        },

        // ========== Collections ==========
        fetchCollections: async () => {
          const response = await CmsService.getOrganizersCmsCollections(
            tenant.organization_slug
          );
          return {
            collections: response?.collections || [],
            count: response?.count || 0,
            total: response?.total || response?.count || 0,
          };
        },

        createCollection: async (data) => {
          return await CmsService.postOrganizersCmsCollections(
            tenant.organization_slug,
            data
          );
        },

        /**
         * Upload media (placeholder for future implementation)
         */
        uploadMedia: async (file: File) => {
          // TODO: Implement using Riptik's media upload API
          throw new Error('Media upload not implemented yet');
        },

        // ========== Page Settings ==========
        listPageSettings: async () => {
          const { PagesService } = await import('../api/generated/services/PagesService');
          const response = await PagesService.getSettingsOrganizationsPages(tenant.organization_slug);
          return {
            pages: response?.pages || [],
          };
        },

        getPageSettings: async (pageName) => {
          const { PagesService } = await import('../api/generated/services/PagesService');
          const config = await PagesService.getSettingsOrganizationsPages1(tenant.organization_slug, pageName);
          return {
            title: config?.title,
            sections: config?.sections,
            source: config?.source,
          };
        },

        updatePageSettings: async (pageName, data) => {
          const { PagesService } = await import('../api/generated/services/PagesService');
          return await PagesService.putSettingsOrganizationsPages(
            tenant.organization_slug,
            pageName,
            data
          );
        },

        deletePageSettings: async (pageName) => {
          const { PagesService } = await import('../api/generated/services/PagesService');
          return await PagesService.deleteSettingsOrganizationsPages(tenant.organization_slug, pageName);
        },

        getTheme: async () => {
          const { SettingsService } = await import('../api/generated/services/SettingsService');
          const theme = await SettingsService.getSettingsOrganizationsTheme(tenant.organization_slug);
          return {
            sectionTypes: theme?.sectionTypes,
          };
        },

        // ========== Organization Settings ==========
        getOrganizationSettings: async () => {
          const { SettingsService } = await import('../api/generated/services/SettingsService');
          const result = await SettingsService.getSettingsOrganizations(tenant.organization_slug);
          const settings: any = result?.settings || {};
          const theme = typeof settings.theme === 'string' ? JSON.parse(settings.theme) : settings.theme || {};
          return {
            hotel_tenant_id: String(tenant.organization?.id ?? tenant.organization_slug),
            hotel_id: settings.hotel_id || '',
            base_theme: theme?.base_theme || 'travel-agency',
          };
        },

        updateOrganizationSettings: async (data) => {
          const { SettingsService } = await import('../api/generated/services/SettingsService');
          const result = await SettingsService.getSettingsOrganizations(tenant.organization_slug);
          const existing: any = result?.settings || {};
          const existingTheme = typeof existing.theme === 'string' ? JSON.parse(existing.theme) : (existing.theme || {});
          const patch: Record<string, any> = {};
          if (data.hotel_id !== undefined) {
            patch.hotel_id = data.hotel_id;
          }
          if (data.base_theme !== undefined) {
            patch.theme = { ...existingTheme, base_theme: data.base_theme };
          }
          await SettingsService.putSettingsOrganizations(tenant.organization_slug, patch as any);
        },

        // ========== Theme CSS Variables ==========
        getThemeCssVars: async () => {
          const { SettingsService } = await import('../api/generated/services/SettingsService');
          const result = await SettingsService.getSettingsOrganizations(tenant.organization_slug);
          const settings: any = result?.settings || {};
          const theme = typeof settings.theme === 'string' ? JSON.parse(settings.theme) : settings.theme || {};
          return (theme?.css_vars ?? {}) as Record<string, string>;
        },

        updateThemeCssVars: async (cssVars) => {
          const { SettingsService } = await import('../api/generated/services/SettingsService');
          const result = await SettingsService.getSettingsOrganizations(tenant.organization_slug);
          const existing: any = result?.settings || {};
          const existingTheme = typeof existing.theme === 'string' ? JSON.parse(existing.theme) : (existing.theme || {});
          await SettingsService.putSettingsOrganizations(tenant.organization_slug, {
            theme: { ...existingTheme, css_vars: cssVars },
          } as any);
        },

        // ========== Domains ==========
        getDomains: async () => {
          const { SettingsService } = await import('../api/generated/services/SettingsService');
          const response = await SettingsService.getSettingsOrganizationsDomains(tenant.organization_slug);
          return { domains: response?.domains || [] };
        },

        updateDomain: async (data) => {
          const { SettingsService } = await import('../api/generated/services/SettingsService');
          return await SettingsService.putSettingsOrganizationsDomains(tenant.organization_slug, data);
        },

        // ========== Menus ==========
        listMenus: async () => {
          const { SettingsMenusService } = await import('../api/generated/services/SettingsMenusService');
          const response = await SettingsMenusService.getSettingsOrganizationsMenus(tenant.organization_slug);
          return {
            menus: response?.menus || [],
          };
        },

        getMenu: async (menuType) => {
          const { SettingsMenusService } = await import('../api/generated/services/SettingsMenusService');
          const items = await SettingsMenusService.getSettingsOrganizationsMenus1(tenant.organization_slug, menuType);
          // Convert array of items to menu object
          return {
            id: menuType,
            name: menuType,
            location: menuType,
            items: items || [],
          };
        },

        createMenu: async (data) => {
          // Menus are predefined (primary, footer, mobile), so "create" is actually an update
          const { SettingsMenusService } = await import('../api/generated/services/SettingsMenusService');
          const response = await SettingsMenusService.putSettingsOrganizationsMenus(
            tenant.organization_slug,
            data.location,
            { items: data.items || [] }
          );
          return {
            id: data.location,
            name: data.name,
            location: data.location,
            items: data.items || [],
          };
        },

        updateMenu: async (menuType, data) => {
          const { SettingsMenusService } = await import('../api/generated/services/SettingsMenusService');
          await SettingsMenusService.putSettingsOrganizationsMenus(
            tenant.organization_slug,
            menuType,
            { items: data.items || [] }
          );
          return {
            id: menuType,
            name: data.name || menuType,
            location: data.location || menuType,
            items: data.items || [],
          };
        },

        deleteMenu: async (menuType) => {
          const { SettingsMenusService } = await import('../api/generated/services/SettingsMenusService');
          await SettingsMenusService.deleteSettingsOrganizationsMenus(tenant.organization_slug, menuType);
        },
      },
    };
  }, [tenant, t, getOrgUrl]);

  // Show loading spinner while tenant is loading
  if (!contextValue) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
      }}>
        <Spin size="large" tip="Loading CMS..." />
      </div>
    );
  }

  return (
    <CMSContext.Provider value={contextValue}>
      {children}
    </CMSContext.Provider>
  );
};

/**
 * Example: Nếu project khác muốn dùng CMS, họ tạo Provider riêng:
 *
 * // MantikCMSProvider.tsx
 * export const MantikCMSProvider: React.FC<{ children }> = ({ children }) => {
 *   const mantikTenant = useMantikTenant(); // Mantik's own hook
 *   const { translate } = useMantikI18n(); // Mantik's own i18n
 *
 *   const contextValue: CMSContextValue = {
 *     tenantId: mantikTenant.organizationId,
 *     organizationSlug: mantikTenant.slug,
 *     locale: mantikTenant.currentLocale,
 *     availableLocales: mantikTenant.supportedLocales,
 *     t: translate,
 *     api: {
 *       fetchLanguages: async () => {
 *         // Call Mantik's GraphQL API
 *         const { data } = await mantikClient.query({ query: GET_LANGUAGES });
 *         return data.languages;
 *       },
 *       fetchOrgAvailableLanguages: async (slug) => {
 *         // Call Mantik's GraphQL API
 *         const { data } = await mantikClient.query({
 *           query: GET_ORG_LANGUAGES,
 *           variables: { slug }
 *         });
 *         return data.organization.languages;
 *       },
 *     },
 *   };
 *
 *   return <CMSContext.Provider value={contextValue}>{children}</CMSContext.Provider>;
 * };
 */
